import Logger from '../logger/logger';
import { cascadePosition } from '../geometry/placement';
import { objectCenter } from '../geometry/fabricRegion';
import { ARC_STRAIGHT } from './arc';

// The panel's arc fields as the arcAmount the text carries: null for no arc,
// otherwise 0-100 with 50 straight (see ./arc).
function arcAmountFrom(data) {
	if (!data.arc) return null;
	const amount = data.arcAmount === null || data.arcAmount === '' ? NaN : Number(data.arcAmount);
	return Number.isFinite(amount) ? Math.min(100, Math.max(0, amount)) : ARC_STRAIGHT;
}

export default class TextTools {
	constructor({ canvasRef, fabric, handleSave, boundingBoxes = [], saveHistory, fitObjectAfterEdit }) {
		this.canvasRef = canvasRef;
		this.fabric = fabric;
		this.handleSave = handleSave;
		this.boundingBoxes = boundingBoxes || [];
		this.saveHistory = saveHistory || function(){};
		// Shrinks an object back inside the print region after an edit that
		// resized it. Supplied by the customizer, which owns the region.
		this.fitObjectAfterEdit = fitObjectAfterEdit || function(){ return false; };
		this.selectedObject = null;
		// The text the parent's panel is still showing from the element that was
		// selected before this one, until it redraws for the new selection. Armed
		// by setSelectedObject, spent by editText - see isSelectionEcho.
		this.pendingEcho = null;
	}

	setSelectedObject(obj) {
		// Picking up another element leaves the parent's panel one beat behind:
		// the fields still hold the element that was selected until now, and
		// anything it sends before it has redrawn describes that one, not this.
		const previous = this.selectedObject;
		if (previous && previous !== obj && typeof previous.text === 'string') {
			this.pendingEcho = previous.text;
		}

		this.selectedObject = obj;
		window.parent.postMessage({ action: 'textSelected', value: {
			text: obj.get('text'), 
			color: obj.get('fill'), 
            fontFamily: obj.get('fontFamily'), 
            fontSize: obj.get('fontSize').toString(),
            textAlign: obj.get('textAlign'),
            cursive: obj.get('fontStyle') === 'italic',
            bold: obj.get('fontWeight') === 'bold',
			arc: obj.arcAmount != null,
			arcAmount: obj.arcAmount != null ? obj.arcAmount : ARC_STRAIGHT
		} }, '*');
	}

	// The size the text actually ended up at, when it is not the size that was
	// asked for. Same string form as textSelected, so the parent's input can
	// take it as-is.
	notifyFontSizeChanged(obj) {
		window.parent.postMessage({ action: 'fontSizeChanged', value: {
			fontSize: obj.get('fontSize').toString()
		} }, '*');
	}

	// Centre `text` in `area`, but step it off anything already sitting there:
	// dropping a second text on the exact centre of the same print area hides
	// the first one, and the customer has no way to tell there are two. Same
	// nudge as cloning, so the two ways of getting a second element behave alike.
	//
	// `bounded: false` for the whole-canvas fallback - there is no print area to
	// keep the text inside of, so the nudge is free to run.
	placeInBox(text, area, opts) {
		const canvas = this.canvasRef.current;
		const width = text.getScaledWidth ? text.getScaledWidth() : text.width;
		const height = text.getScaledHeight ? text.getScaledHeight() : text.height;

		const taken = (canvas && canvas.getObjects ? canvas.getObjects() : [])
			.filter(o => o !== text && (o.type === 'text' || o.type === 'image'))
			.map(objectCenter);

		const center = cascadePosition({
			center: { x: area.left + area.width / 2, y: area.top + area.height / 2 },
			size: { width, height },
			bounds: (!opts || opts.bounded !== false) ? area : null,
			taken
		});

		text.set({ left: center.x - width / 2, top: center.y - height / 2 });
	}

	async addText(data, boundingBox) {
		console.log("boundingbox", boundingBox);
		const canvas = this.canvasRef.current;
		const fabric = this.fabric;
		if (!canvas || !fabric) return null;
		
		let initText = 'New Text';
		let initColor = '#000000';
		let initFontFamily = 'Arial';
		let initFontSize = 40;
		let initALign = 'center';
		let initCursive = false;
		let initBold = false;
		let initUnderline = false;
		
		if(data.text){
			initText = data.text;
		}
		if(data.color){
			initColor = data.color;
		}
		if(data.fontFamily){
			initFontFamily = data.fontFamily;
		}
		if(data.fontSize){
			initFontSize = data.fontSize;
		}
		if(data.textAlign){
			initALign = data.textAlign;
		}
		if(data.cursive){
			initCursive = data.cursive;
		}
		if(data.bold){
			initBold = data.bold;
		}

		if(data.underline){
			initUnderline = data.underline;
		}
		
		try {
			await document.fonts.load(`1em '${initFontFamily}'`);
		} catch (e) {}

		const text = new fabric.Text(initText, {
			left: canvas.width / 2,
			top: canvas.height / 2,
			fill: initColor,
			fontFamily: initFontFamily, 
			fontSize: initFontSize,
			textAlign: initALign,
			fontStyle: initCursive ? 'italic' : 'normal',
			fontWeight: initBold ? 'bold' : 'normal',
			underline: initUnderline,
			// The arc follows the text: its path is rebuilt from this on every
			// re-measure, so it keeps the size the straight text has.
			arcAmount: arcAmountFrom(data),
			clamped: false
		});

		// The box the text is placed in: the one that was clicked, otherwise the
		// side's default. Both branches used to carry their own copy of the fit
		// and centring below.
		const box = boundingBox
			|| (this.boundingBoxes.length > 0
				? (this.boundingBoxes.find(b => b.default === true) || this.boundingBoxes[0])
				: null);

		if (box) {
			if(text.width > box.width || text.height > box.height){
				const scale = Math.min(box.width / text.width, box.height / text.height);
				text.set({ fontSize: Math.floor(text.fontSize * scale) });
			}
			this.placeInBox(text, box);
		} else {
			this.placeInBox(text, { left: 0, top: 0, width: canvas.width, height: canvas.height }, { bounded: false });
		}

		canvas.add(text);

		// Defer selection/render to avoid DOM mutation races with React
		setTimeout(() => {
			try {
				canvas.setActiveObject(text);
				canvas.renderAll();
			} catch (e) {
				console.warn('TextTools.addText: could not set active object immediately', e);
			}
		}, 0);

		this.selectedObject = text;
		// Defer save to next tick so any external state/refs (designs, refs) are up-to-date
		setTimeout(() => {
			try { this.handleSave && this.handleSave(); } catch (e) {}
		}, 0);
		try { Logger('Text added'); } catch (e) {}
		return text;
	}

	// The edit that arrives on the heels of a selection change and would put the
	// previously selected element's text onto the newly selected one. That is the
	// parent's panel emptying its old state, never something the customer typed:
	// typing moves the text a character at a time, it does not jump from one
	// element's content to another's. Applying it rewrote the element the customer
	// had only just clicked on.
	//
	// The guard stays armed until an edit arrives that is not the echo, so a panel
	// that sends its stale fields more than once cannot slip one past on the
	// second message. The one input it costs is pasting the other element's exact
	// text as the first thing done to a fresh selection.
	isSelectionEcho(data) {
		if (this.pendingEcho === null) return false;
		if (typeof data.text === 'string'
			&& data.text === this.pendingEcho
			&& data.text !== this.selectedObject.text) return true;
		this.pendingEcho = null;
		return false;
	}

	async editText(data) {
		if (!this.selectedObject || this.selectedObject.type !== 'text') return false;
		if (this.isSelectionEcho(data)) return false;

		// Keep the text centred where it is. Read the centre through fabric so
		// it stays correct at any scale - left + width / 2 ignores scaleX.
		const center = this.selectedObject.getCenterPoint();

		// Snapshot for the one case that is still refused: text that does not fit
		// even at MIN_FONT_SIZE. Everything up to that point is accepted and the
		// font size is shrunk to make room (see fitObjectAfterEdit), so this only
		// bites at the floor - and the shrink applied on the way there has to be
		// rolled back together with the keystroke that caused it.
		const previousText = this.selectedObject.text;
		const previousFontSize = this.selectedObject.fontSize;

		if(data.text){
			this.selectedObject.set({ text: data.text });
		}
		if(data.color){
			this.selectedObject.set({ fill: data.color });
		}
		if(data.fontFamily){
			let fontFace = null;
			try {
				await document.fonts.load(`1em '${data.fontFamily}'`).then(() => {
					fontFace = document.fonts.get(`1em '${data.fontFamily}'`);
					console.log('Loaded font:', fontFace);
				});
			} catch (e) {}
			this.selectedObject.set({ fontFamily: data.fontFamily });
		}
		if(data.fontSize){
			this.selectedObject.set({ fontSize: data.fontSize });
		}
		if(data.textAlign){
			this.selectedObject.set({ textAlign: data.textAlign });
		}
		if(data.cursive){
			this.selectedObject.set({ fontStyle: data.cursive ? 'italic' : 'normal' });
		}else{
			this.selectedObject.set({ fontStyle: 'normal' });
		}
		if(data.bold){
			this.selectedObject.set({ fontWeight: data.bold ? 'bold' : 'normal' });
		}else{
			this.selectedObject.set({ fontWeight: 'normal' });
		}

		if(data.underline){
			this.selectedObject.set({ underline: data.underline });
		}else{
			this.selectedObject.set({ underline: false });
		}
		const arcAmount = arcAmountFrom(data);
		if (arcAmount !== null) {
			this.selectedObject.set({ arcAmount });
		} else {
			this.selectedObject.set({ arcAmount: null, path: null });
		}
		
		this.selectedObject.setPositionByOrigin(center, 'center', 'center');
		this.selectedObject.setCoords();

		// Shrink back inside the print region if the new size overflows it.
		// Unconditional: the region is what constrains the text, and there is
		// not always an active bounding box (it is only set by clicking one).
		const requestedFontSize = this.selectedObject.fontSize;
		const locked = this.fitObjectAfterEdit(this.selectedObject);

		// Locked means the shrink ran out of room. Text that does not get shorter
		// is the only edit we can undo on the customer's behalf: a deletion has
		// to go through even while locked - it is how they get back out - and a
		// colour or font family that happens to overflow is still applied, at the
		// floor size.
		if (locked && data.text && data.text !== previousText && data.text.length >= previousText.length) {
			this.selectedObject.set({ text: previousText, fontSize: previousFontSize });
			this.selectedObject.setPositionByOrigin(center, 'center', 'center');
			this.selectedObject.setCoords();
			// Refits the restored text, which also clears the lock flag - so
			// re-assert it: the input the customer just typed was refused.
			this.fitObjectAfterEdit(this.selectedObject);
			this.selectedObject.clamped = true;
			window.parent.postMessage({ action: 'scalingLocked', value: true }, '*');
		}

		// Tell the parent the size we could actually honour, so its input does
		// not keep re-sending a font size that does not fit.
		if (this.selectedObject.fontSize !== requestedFontSize) {
			this.notifyFontSizeChanged(this.selectedObject);
		}

		const canvas = this.canvasRef.current;
		canvas.renderAll();

		// A preview only shows the change on the element: the parent sends the
		// edit for real (preview false) once the customer settles on it, and that
		// one is what goes into history and gets saved.
		if (data.preview === true) return true;

		try { this.saveHistory(); } catch (e) {}
		try { this.handleSave(); } catch (e) {}
		try { Logger('Text edited'); } catch (e) {}
		return true;
	}
}